import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Search, Code, Users, Cloud, Network, AlertTriangle } from 'lucide-react';

export default function CyberServicesPage() {
  const [hoveredItem, setHoveredItem] = useState(null);

  const services = [
    {
      id: 'risk-assessment',
      title: 'Risk Assessment Services',
      icon: AlertTriangle,
      description: 'Comprehensive security risk evaluation and mitigation strategies',
      gradient: 'linear-gradient(135deg, #ef4444, #ec4899)',
      route: '/services/risk-assessment'
    },
    {
      id: 'source-code-review',
      title: 'Source Code Review',
      icon: Code,
      description: 'In-depth analysis of your codebase for security vulnerabilities',
      gradient: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
      route: '/services/source-code-review'
    },
    {
      id: 'third-party-risk',
      title: 'Third-Party Risk Management',
      icon: Users,
      description: 'Vendor security assessment and ongoing risk monitoring',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      route: '/services/third-party-risk'
    },
    {
      id: 'vapt',
      title: 'VAPT Services',
      icon: Search,
      description: 'Vulnerability Assessment and Penetration Testing solutions',
      gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      route: '/services/vapt'
    },
    {
      id: 'cloud-security',
      title: 'Cloud Security Services',
      icon: Cloud,
      description: 'Secure cloud infrastructure and compliance management',
      gradient: 'linear-gradient(135deg, #f97316, #dc2626)',
      route: '/services/cloud-security'
    },
    {
      id: 'network-security',
      title: 'Network Security Services',
      icon: Network,
      description: 'Advanced network protection and monitoring solutions',
      gradient: 'linear-gradient(135deg, #14b8a6, #2563eb)',
      route: '/services/network-security'
    },
    {
      id: 'cyber-defense',
      title: 'Cyber Defense & Incident Response',
      icon: Shield,
      description: '24/7 threat monitoring and rapid incident response',
      gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
      route: '/services/cyber-defense'
    }
  ];

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f9fafb, #dbeafe)',
    padding: '80px 16px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '64px',
    maxWidth: '1200px',
    margin: '0 auto 64px auto'
  };

  const iconContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
    borderRadius: '50%',
    marginBottom: '24px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const titleStyle = {
    fontSize: '3.5rem',
    fontWeight: '900',
    background: 'linear-gradient(135deg, #1f2937, #1e40af, #4338ca)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '24px',
    lineHeight: '1.1'
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    color: '#4b5563',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const dividerStyle = {
    width: '96px',
    height: '4px',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    margin: '32px auto 0 auto',
    borderRadius: '2px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '32px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const getCardStyle = (serviceId) => ({
    position: 'relative',
    height: '100%',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '32px',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transform: hoveredItem === serviceId ? 'scale(1.05)' : 'scale(1)',
    backgroundColor: hoveredItem === serviceId ? '#f9fafb' : '#ffffff',
    borderColor: hoveredItem === serviceId ? '#d1d5db' : '#e5e7eb'
  });

  const getIconStyle = (gradient) => ({
    width: '64px',
    height: '64px',
    background: gradient,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transform: hoveredItem ? 'scale(1.1)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  });

  const serviceTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '12px'
  };

  const serviceDescStyle = {
    fontSize: '0.875rem',
    color: '#6b7280',
    lineHeight: '1.5',
    marginBottom: '24px'
  };

  const learnMoreStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#2563eb'
  };

  const ctaContainerStyle = {
    textAlign: 'center',
    marginTop: '80px',
    maxWidth: '1200px',
    margin: '80px auto 0 auto'
  };

  const ctaBoxStyle = {
    background: 'linear-gradient(135deg, #dbeafe, #e0e7ff)',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '48px 32px',
    maxWidth: '600px',
    margin: '0 auto',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  };

  const ctaTitleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '16px'
  };

  const ctaTextStyle = {
    fontSize: '1.125rem',
    color: '#4b5563',
    marginBottom: '32px'
  };

  const buttonStyle = {
    background: 'linear-gradient(135deg, #2563eb, #4338ca)',
    color: '#ffffff',
    fontWeight: '700',
    padding: '16px 32px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={iconContainerStyle}>
          <Shield style={{ width: '40px', height: '40px', color: '#ffffff' }} />
        </div>
        <h1 style={titleStyle}>
          Cyber Security<br />
          <span style={{ fontSize: '2.5rem' }}>Services</span>
        </h1>
        <p style={subtitleStyle}>
          Protecting your digital assets with cutting-edge security solutions and expert guidance
        </p>
        <div style={dividerStyle}></div>
      </div>

      <div style={gridStyle}>
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <Link
              to={service.route}
              key={service.id}
              style={getCardStyle(service.id)}
              onMouseEnter={() => setHoveredItem(service.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div style={getIconStyle(service.gradient)}>
                <Icon style={{ width: '32px', height: '32px', color: '#ffffff' }} />
              </div>

              <div>
                <h3 style={serviceTitleStyle}>
                  {service.title}
                </h3>
                <p style={serviceDescStyle}>
                  {service.description}
                </p>
                
                <div style={learnMoreStyle}>
                  <span>Learn More</span>
                  <svg style={{ width: '16px', height: '16px', marginLeft: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div style={ctaContainerStyle}>
  <div style={ctaBoxStyle}>
    <h2 style={ctaTitleStyle}>
      Ready to Secure Your Business?
    </h2>
    <p style={ctaTextStyle}>
      Get started with a comprehensive security assessment tailored to your needs.
    </p>
    <Link to="/contact" style={buttonStyle}>
      CONTACT US →
    </Link>
  </div>
</div>

    </div>
  );
}